import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getSchemeDetail,
  createScheme,
  updateScheme,
  getCourseOptions,
  getSubjectOptions,
  getAcademicYears,
} from '../api/schemesApi';
import { appToast } from '../../../../components/AppToast';

function getDefaultInstallmentDate(index) {
  const d = new Date();
  d.setMonth(d.getMonth() + index + 1);
  return d.toISOString().split('T')[0];
}

function generateDefaultInstallments(count) {
  const n = Math.max(1, Math.min(30, count));
  const base = Math.floor(100 / n);
  const remainder = 100 - base * (n - 1);
  const rows = [];
  for (let i = 0; i < n; i++) {
    rows.push({
      installment_date: getDefaultInstallmentDate(i),
      percentage_amt: String(i === n - 1 ? remainder : base),
    });
  }
  return rows;
}

export function useSchemesEntryScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editingSchemeId = searchParams.get('id');

  const [courseOptions, setCourseOptions] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const ayearOptions = useMemo(() => getAcademicYears(), []);
  const defaultAyear = ayearOptions[0]?.value || '2026-27';

  const [form, setForm] = useState({
    name: '',
    course_selection: '1',
    max_discount: '0',
    combo_grid: [
      {
        ayear: defaultAyear,
        course_id: '',
        admission_fees: '0',
        tution_fees: '0',
        courseware_fees: '0',
        percentage: '100',
      },
    ],
    has_subjects: false,
    subjects: [],
    has_installment: false,
    no_of_installments: '3',
    installment_grid: generateDefaultInstallments(3),
  });

  const [operationalStatus, setOperationalStatus] = useState('active');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load dropdown options (Courses, Subjects)
  useEffect(() => {
    let mounted = true;
    async function loadOptions() {
      try {
        const [cOpts, sOpts] = await Promise.all([getCourseOptions(), getSubjectOptions()]);
        if (mounted) {
          setCourseOptions(cOpts);
          setSubjectOptions(sOpts);
        }
      } catch (err) {
        console.error('Failed to load scheme form options:', err);
      }
    }
    loadOptions();
    return () => {
      mounted = false;
    };
  }, []);

  // Load Scheme record in Edit mode
  useEffect(() => {
    async function loadDetail() {
      if (!editingSchemeId) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError('');
      try {
        const detail = await getSchemeDetail(editingSchemeId);
        if (detail) {
          const courseRows = (detail.course_items || []).map((ci) => ({
            id: ci.id,
            ayear: ci.ayear || defaultAyear,
            course_id: String(ci.course_id || ''),
            admission_fees: String(ci.admission_fees ?? 0),
            tution_fees: String(ci.tution_fees ?? 0),
            courseware_fees: String(ci.courseware_fees ?? 0),
            percentage: String(ci.percentage ?? 100),
          }));

          const instRows = (detail.installment_items || []).map((ii) => ({
            id: ii.id,
            installment_date: ii.installment_date || '',
            percentage_amt: String(ii.percentage_amt ?? 0),
          }));

          const subList = detail.subjects_csv
            ? detail.subjects_csv
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            : [];

          setForm({
            name: detail.name || '',
            course_selection: String(courseRows.length > 0 ? courseRows.length : 1),
            max_discount: String(detail.max_discount ?? 0),
            combo_grid:
              courseRows.length > 0
                ? courseRows
                : [
                    {
                      ayear: defaultAyear,
                      course_id: '',
                      admission_fees: '0',
                      tution_fees: '0',
                      courseware_fees: '0',
                      percentage: '100',
                    },
                  ],
            has_subjects: subList.length > 0,
            subjects: subList,
            has_installment: instRows.length > 0,
            no_of_installments: String(instRows.length > 0 ? instRows.length : 3),
            installment_grid: instRows.length > 0 ? instRows : generateDefaultInstallments(3),
          });
          setOperationalStatus(detail.status === 'Active' ? 'active' : 'inactive');
        } else {
          setError('Scheme not found.');
          appToast.error('Scheme not found.');
        }
      } catch (err) {
        console.error('Failed to load scheme:', err);
        const msg = err?.response?.data?.detail || 'Failed to load scheme details.';
        setError(msg);
        appToast.error(msg);
      } finally {
        setIsLoading(false);
      }
    }
    loadDetail();
  }, [editingSchemeId, defaultAyear]);

  // Adjust combo grid rows when course_selection changes
  const handleCourseSelectionChange = (countStr) => {
    const desired = Math.max(1, Math.min(9, Number(countStr) || 1));
    setForm((prev) => {
      let grid = [...prev.combo_grid];
      if (desired > grid.length) {
        for (let i = grid.length; i < desired; i++) {
          grid.push({
            ayear: defaultAyear,
            course_id: '',
            admission_fees: '0',
            tution_fees: '0',
            courseware_fees: '0',
            percentage: '100',
          });
        }
      } else if (desired < grid.length) {
        grid = grid.slice(0, desired);
      }
      return { ...prev, course_selection: String(desired), combo_grid: grid };
    });
  };

  // Adjust installment rows when no_of_installments changes
  const handleInstallmentCountChange = (countStr) => {
    const count = Math.max(1, Math.min(30, Number(countStr) || 1));
    setForm((prev) => ({
      ...prev,
      no_of_installments: String(count),
      installment_grid: generateDefaultInstallments(count),
    }));
  };

  const patchForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateGridRow = (index, field, value) => {
    setForm((prev) => {
      const newGrid = [...prev.combo_grid];
      newGrid[index] = { ...newGrid[index], [field]: value };
      return { ...prev, combo_grid: newGrid };
    });
  };

  const updateInstallmentRow = (index, field, value) => {
    setForm((prev) => {
      const newGrid = [...prev.installment_grid];
      newGrid[index] = { ...newGrid[index], [field]: value };
      return { ...prev, installment_grid: newGrid };
    });
  };

  const toggleSubject = (subjectId) => {
    setForm((prev) => {
      const list = [...prev.subjects];
      const idx = list.indexOf(subjectId);
      if (idx === -1) {
        list.push(subjectId);
      } else {
        list.splice(idx, 1);
      }
      return { ...prev, subjects: list };
    });
  };

  const totals = useMemo(() => {
    let totalAdmission = 0;
    let totalTuition = 0;
    let totalCourseware = 0;
    let totalPercentage = 0;

    form.combo_grid.forEach((row) => {
      totalAdmission += Number(row.admission_fees) || 0;
      totalTuition += Number(row.tution_fees) || 0;
      totalCourseware += Number(row.courseware_fees) || 0;
      totalPercentage += Number(row.percentage) || 0;
    });

    return {
      totalAdmission,
      totalTuition,
      totalCourseware,
      totalPercentage: totalPercentage.toFixed(2),
    };
  }, [form.combo_grid]);

  const installmentTotal = useMemo(() => {
    let total = 0;
    form.installment_grid.forEach((row) => {
      total += Number(row.percentage_amt) || 0;
    });
    return total.toFixed(2);
  }, [form.installment_grid]);

  const completionPercent = useMemo(() => {
    let score = 0;
    if (form.name.trim()) score += 30;
    const coursesSelected = form.combo_grid.filter((r) => r.course_id).length;
    if (coursesSelected > 0) score += 30;
    if (form.has_subjects && form.subjects.length > 0) score += 20;
    else if (!form.has_subjects) score += 20;
    if (form.has_installment && form.installment_grid.length > 0) score += 20;
    else if (!form.has_installment) score += 20;
    return Math.min(100, score);
  }, [form]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      const msg = 'Scheme Name is required.';
      setError(msg);
      appToast.error(msg);
      return;
    }

    const missingCourse = form.combo_grid.some((r) => !r.course_id);
    if (missingCourse) {
      const msg = 'Please select a Course for all configured course rows.';
      setError(msg);
      appToast.error(msg);
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        name: form.name.trim(),
        max_discount: Number(form.max_discount) || 0,
        subjects_csv: form.has_subjects ? form.subjects.join(',') : '',
        status: operationalStatus === 'active' ? 'Active' : 'Archive',
        course_items: form.combo_grid.map((r) => ({
          course_id: Number(r.course_id),
          ayear: r.ayear,
          admission_fees: Number(r.admission_fees) || 0,
          tution_fees: Number(r.tution_fees) || 0,
          courseware_fees: Number(r.courseware_fees) || 0,
          percentage: Number(r.percentage) || 100,
        })),
        installment_items: form.has_installment
          ? form.installment_grid.map((r) => ({
              installment_date: r.installment_date || null,
              percentage_amt: Number(r.percentage_amt) || 0,
            }))
          : [],
      };

      if (editingSchemeId) {
        await updateScheme(editingSchemeId, payload);
        const msg = 'Scheme updated successfully.';
        appToast.success(msg);
        setSuccess(msg);
      } else {
        await createScheme(payload);
        const msg = 'Scheme created successfully.';
        appToast.success(msg);
        setSuccess(msg);
      }

      setTimeout(() => {
        navigate('/masters/schemes/list');
      }, 600);
    } catch (err) {
      console.error('Failed to save scheme:', err);
      const msg = err?.response?.data?.detail || 'Failed to save scheme.';
      const errStr = typeof msg === 'string' ? msg : JSON.stringify(msg);
      setError(errStr);
      appToast.error(errStr);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    state: {
      form,
      operationalStatus,
      isLoading,
      isSaving,
      error,
      success,
      isEditMode: Boolean(editingSchemeId),
      courseOptions,
      subjectOptions,
      ayearOptions,
      totals,
      installmentTotal,
      completionPercent,
    },
    actions: {
      patchForm,
      setOperationalStatus,
      handleCourseSelectionChange,
      handleInstallmentCountChange,
      updateGridRow,
      updateInstallmentRow,
      toggleSubject,
      handleSave,
    },
  };
}
